import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  REPLACEMENT_KIND_LABELS,
  REPLACEMENT_STATUSES,
  REPLACEMENT_STATUS_LABELS,
  roleCanManageReplacements,
  type PublicReplacementRequest,
  type ReplacementResponse,
  type ReplacementStatus,
} from '@inventohub/shared';
import { ApiClientError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';

export function ReplacementDetailPage(): JSX.Element {
  const { user } = useAuth();
  const canManage = user ? roleCanManageReplacements(user.role) : false;
  const params = useParams();
  const id = params.id ?? '';
  const [row, setRow] = useState<PublicReplacementRequest | null>(null);
  const [status, setStatus] = useState<ReplacementStatus>('open');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiRequest<ReplacementResponse>(`/api/replacements/${id}`);
        if (cancelled) return;
        setRow(body.replacement);
        setStatus(body.replacement.status);
        setNotes(body.replacement.resolutionNotes ?? '');
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(
            caught instanceof ApiClientError
              ? caught.message
              : 'Could not load this replacement request.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function onSave(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!row) return;
    setError(null);
    setNotice(null);
    try {
      const body = await apiRequest<ReplacementResponse>(`/api/replacements/${row.id}`, {
        method: 'PATCH',
        body: { status, resolutionNotes: notes || null },
      });
      setRow(body.replacement);
      setStatus(body.replacement.status);
      setNotes(body.replacement.resolutionNotes ?? '');
      setNotice('Saved.');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not save the request.');
    }
  }

  return (
    <section data-testid="replacement-detail">
      <div className="card">
        <h2>
          {row
            ? `${REPLACEMENT_KIND_LABELS[row.kind]} · ${row.asset?.serial ?? row.tote?.number ?? row.id}`
            : 'Replacement request'}
        </h2>
        {error ? (
          <p className="status status--error" data-testid="replacement-detail-error">
            {error}
          </p>
        ) : null}
        {notice ? <p className="status status--ok">{notice}</p> : null}
        {row ? (
          <>
            <p className="meta">
              {REPLACEMENT_STATUS_LABELS[row.status]} · requested by {row.requestedBy.name}
            </p>
            {row.event ? <p className="meta">Event: {row.event.name}</p> : null}
            {row.tote ? (
              <p>
                <Link to={`/totes/${encodeURIComponent(row.tote.number)}`}>
                  Tote {row.tote.number}
                </Link>
              </p>
            ) : null}
            {row.asset ? (
              <p>
                <Link to={`/assets/${encodeURIComponent(row.asset.serial)}`}>
                  Asset {row.asset.serial}
                </Link>
              </p>
            ) : null}
            <p>{row.description}</p>
          </>
        ) : null}
      </div>
      {canManage && row ? (
        <form className="card" onSubmit={(event) => void onSave(event)}>
          <h2>Update status</h2>
          <label className="field">
            Status
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value as ReplacementStatus)}
              data-testid="replacement-status"
            >
              {REPLACEMENT_STATUSES.map((option) => (
                <option key={option} value={option}>
                  {REPLACEMENT_STATUS_LABELS[option]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            Resolution notes
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              data-testid="replacement-resolution"
            />
          </label>
          <button type="submit" className="button" data-testid="replacement-save">
            Save
          </button>
        </form>
      ) : null}
    </section>
  );
}
